import type { MessageSchema } from './sl'

/*
 * English — the second language and the fallback. Exactly the keys of `sl.ts` (checked by
 * `satisfies MessageSchema`); plural messages carry two forms (one | other).
 */
export default {
    general: {
        add:           'Add',
        edit:          'Edit',
        delete:        'Delete',
        cancel:        'Cancel',
        save:          'Save',
        close:         'Close',
        confirm:       'Confirm',
        search:        'Search …',
        loading:       'Loading …',
        saving:        'Saving',
        clear_filters: 'Clear filters',
        sign_out:      'Sign out',
    },
    brand: {
        product_name: 'Atraktivna portal',
        tagline:      'Tasks',
    },
    nav: {
        group_naloge: 'Tasks',
        dashboard:   'Overview',
        my_tasks:    'My tasks',
        projects:    'Projects',
        timeline:    'Timeline',
        group_admin: 'Administration',
        users:       'Users',
        settings:    'Settings',
        profile:     'Profile',
    },
    layout: {
        open_menu:  'Open menu',
        close_menu: 'Close menu',
    },
    color_mode: {
        switch_to_light: 'Switch to light mode',
        switch_to_dark:  'Switch to dark mode',
    },
    module: {
        naloge:       'Tasks',
        role_manager: 'Manager',
        role_member:  'Member',
        off:          'Off',
        portal_admin: 'Portal administrator',
        none:         'No modules',
        access:       'Access',
        access_help:  'A portal administrator manages your access.',
    },
    locale: {
        label: 'Language',
        sl:    'Slovenščina',
        en:    'English',
    },
    errors: {
        generic_error: 'Something went wrong.',
        invalid_data:  'The data is not valid.',
        not_found:     'It does not exist (any more).',
        unauthorized:  'Your session has expired. Please sign in again.',
        forbidden:     'You are not allowed to do that.',
        on_add:        'Adding failed.',
        on_edit:       'Saving failed.',
        on_delete:     'Deleting failed.',
        on_load:       'Loading failed.',
    },
    success: {
        add:    'Added.',
        edit:   'Saved.',
        delete: 'Deleted.',
    },
    validation: {
        required: 'This field is required.',
        email:    'Invalid e-mail address.',
        too_long: 'Too long.',
        invalid:  'Invalid value.',
    },
    date: {
        pick:  'Pick a date',
        clear: 'Clear the date',
    },
    auth: {
        username:          'Username',
        password:          'Password',
        password_show:     'Show password',
        password_hide:     'Hide password',
        sign_in:           'Sign in',
        or:                'or',
        failed:            'Wrong username or password.',
        too_many_attempts: 'Too many failed attempts. Try again in a few minutes.',
    },
    passkey: {
        passkeys:        'Passkeys',
        subtitle:        'Sign in with a fingerprint, your face or a security key instead of a password.',
        sign_in:         'Sign in with a passkey',
        sign_in_failed:  'Signing in with a passkey failed.',
        add:             'Add a passkey',
        added:           'The passkey was added.',
        failed:          'The passkey could not be added.',
        name:            'Name',
        name_help:       'The name you will know the key by in the list, e.g. "Work laptop".',
        default_name:    'My device',
        continue:        'Continue',
        created:         'Added',
        last_used:       'Last used',
        never_used:      'Not used yet',
        empty:           'You have no passkeys yet.',
        delete:          'Delete passkey',
        confirm_delete:  'Delete the passkey "{name}"? That device will no longer sign you in without a password.',
        removed:         'The passkey was deleted.',
        delete_failed:   'The passkey could not be deleted.',
        unavailable:     'Passkeys are not set up on this server.',
        unsupported:     'This browser cannot use passkeys (a secure HTTPS connection is required).',
    },
    password: {
        change:        'Change password',
        current:       'Current password',
        new:           'New password',
        repeat:        'Repeat the new password',
        mismatch:      'The passwords do not match.',
        changed:       'Your password was changed.',
        failed:        'The password could not be changed.',
        wrong_current: 'The current password is not correct.',
    },
    profile: {
        title:             'My profile',
        subtitle:          'The name and address your colleagues see and your mail goes to.',
        account:           'Account',
        notify_email:      'E-mail notifications',
        notify_email_help: 'Mail when somebody assigns you a task or comments on it, and a daily reminder of due dates.',
        locale_help:       'The language of the app and of your e-mails.',
    },
    user: {
        users:                'Users',
        subtitle:             'Who can sign in to the portal and what they can use.',
        add:                  'New user',
        edit:                 'Edit user',
        delete:               'Delete user',
        name:                 'Full name',
        username:             'Username',
        username_rule:        '3–32 characters: lower-case letters, digits, dot, underscore or dash.',
        username_fixed:       'The username cannot be changed.',
        username_taken:       'That username already exists.',
        email:                'E-mail',
        portal_admin_help:    'Users and portal settings. On its own it allows nothing inside the modules.',
        portal_admin_yes:     'Yes',
        module_naloge_help:   'A manager sees and edits every project, a member only their own.',
        capacity:             'Capacity',
        capacity_help:        'Working hours a day the timeline counts with.',
        capacity_value:       '{hours} h/day',
        capacity_range:       'Between 0.5 and 24 hours.',
        password:             'Password',
        password_reset:       'New password (reset)',
        password_keep:        'Leave empty to keep the current password.',
        password_rule:        'At least 8 characters.',
        password_too_short:   'The password must have at least 8 characters.',
        active:               'Active',
        active_help:          'An inactive user cannot sign in and is not offered as an assignee.',
        status:               'Status',
        status_active:        'Active',
        status_inactive:      'Inactive',
        last_login:           'Last sign-in',
        never_logged_in:      'Never',
        search:               'Search by name or e-mail …',
        show_inactive:        'Show inactive',
        empty:                'No users.',
        you:                  'You',
        cannot_delete_self:   'You cannot delete your own account.',
        confirm_delete:       'Delete the user {name}? Their tasks stay and keep their assignee.',
        deleted:              'The user was deleted.',
        delete_failed:        'The user could not be deleted.',
        last_admin:           'This is the last active portal administrator — at least one must remain.',
        locale:               'Language',
        locale_help:          'The language of the app and of the e-mails for this user.',
    },
    mail: {
        title:                       'E-mail',
        subtitle:                    'The server notifications and daily reminders are sent through.',
        enabled:                     'Send e-mail',
        enabled_help:                'Off: notifications stay inside the app.',
        host:                        'SMTP server',
        host_placeholder:            'smtp.example.com',
        port:                        'Port',
        tls:                         'Encryption',
        tls_starttls:                'STARTTLS',
        tls_tls:                     'TLS',
        tls_none:                    'None',
        username:                    'Username',
        username_help:               'Empty when the server needs no sign-in.',
        password:                    'Password',
        password_help:               'Stored encrypted and never shown again.',
        password_stored:             'A password is stored. An empty field keeps it, anything typed replaces it.',
        password_stored_placeholder: '••••••••',
        from_address:                'Sender address',
        from_address_placeholder:    "naloge{'@'}atraktivna.si",
        from_name:                   'Sender name',
        base_url:                    'Application address',
        base_url_help:               'Links in e-mails lead here, e.g. https://tasks.atraktivna.dev.it-team.dev.',
        base_url_invalid:            'The address must start with http:// or https://.',
        reminder_hour:               'Daily reminder hour',
        reminder_hour_help:          'At this hour (Ljubljana time) the reminder of tomorrow’s deadlines and overdue tasks goes out.',
        required_when_enabled:       'Required while sending is switched on.',
        test_title:                  'Test message',
        test_subtitle:               'Sent through the SAVED settings — save your changes first.',
        test_to:                     'Recipient',
        test_send:                   'Send a test',
        test_sent:                   'A test message was sent to {to}.',
        not_configured:              'E-mail is not set up or not switched on.',
        failed:                      'The message could not be sent.',
        failed_reply:                'The SMTP server refused the message: {reply}',
    },
    notification: {
        title:         'Notifications',
        title_unread:  'Notifications (unread: {count})',
        mark_all_read: 'Mark all as read',
        mark_failed:   'The notifications could not be marked.',
        empty:         'No notifications.',
        unread:        'Unread',
        assigned:      'You were assigned',
        commented:     'New comment on',
        due_soon:      'Due tomorrow:',
        overdue:       'Overdue:',
    },
    activity: {
        empty:                'No activity yet.',
        system:               'System',
        load_more:            'Load older',
        task_created:         'New task',
        task_updated:         'Task updated',
        status_changed:       'Status: {from} → {to}',
        assigned:             'Assignee: {from} → {to}',
        schedule_changed:     'Dates: {from} → {to}',
        cascade_from:         '(because of {key})',
        dependencies_changed: 'Dependencies changed',
        comment_added:        'New comment',
        attachment_added:     'New attachment',
        attachment_removed:   'Attachment removed',
        task_deleted:         'Task deleted',
        project_created:      'New project',
        project_updated:      'Project updated',
        members_changed:      'Project members changed',
    },
    schedule: {
        shifted_title: '{n} dependent task was moved | {n} dependent tasks were moved',
    },
    dashboard: {
        stat_open:     'Open tasks',
        stat_overdue:  'Overdue',
        stat_today:    'Today',
        stat_week:     'This week',
        my_tasks:      'My tasks',
        all_my_tasks:  'All my tasks',
        today_is:      'Today is {day}',
        group_overdue: 'Overdue',
        group_today:   'Today',
        group_week:    'This week',
        nothing_due:   'Nothing of yours is due this week. Nice!',
        projects:      'Projects',
        all_projects:  'All projects',
        activity:      'Recent activity',
    },
    my_tasks: {
        title:         'My tasks',
        subtitle:      'The tasks assigned to you, by due date.',
        scope_open:    'Open',
        scope_all:     'All',
        empty:         'You have no assigned tasks.',
        group_overdue: 'Overdue',
        group_today:   'Today',
        group_week:    'This week',
        group_later:   'Later',
        group_none:    'No due date',
        group_done:    'Done',
    },
    project: {
        projects:               'Projects',
        subtitle:               'The projects you can see.',
        search:                 'Search by key, name or lead …',
        empty:                  'No projects.',
        add:                    'New project',
        edit:                   'Edit project',
        delete:                 'Delete project',
        deleted:                'The project was deleted.',
        confirm_delete:         'Delete the project {key} {name}? All its tasks go with it.',
        key:                    'Key',
        key_help:               '2–6 characters, e.g. NAB.',
        key_rule:               '2–6 upper-case letters or digits, starting with a letter.',
        key_taken:              'Another project already uses that key.',
        name:                   'Name',
        description:            'Description',
        status:                 'Status',
        status_filter:          'Project status',
        status_active:          'Active',
        status_on_hold:         'On hold',
        status_done:            'Done',
        status_archived:        'Archived',
        filter_current:         'All but archived',
        filter_all:             'All, archived too',
        lead:                   'Project lead',
        lead_help:              'The lead can edit the project and its members.',
        lead_me:                'Me (default)',
        lead_none:              'No lead',
        start_date:             'Start',
        due_date:               'Due',
        dates:                  'Dates',
        members:                'Members',
        members_help:           'Only members can be given the project’s tasks. The lead is a member automatically.',
        members_placeholder:    'Pick colleagues …',
        members_readonly:       'Members are edited by an administrator, a manager or the project lead.',
        members_unsaved:        'The member changes are not saved yet.',
        members_saved:          'The members were saved.',
        members_failed:         'The members could not be saved.',
        add_member:             'Add',
        add_member_placeholder: 'Add a colleague …',
        remove_member:          'Remove {name}',
        role:                   'Project role',
        role_lead:              'Project lead',
        role_member:            'Member',
        tasks:                  'Tasks',
        progress:               'Progress',
        progress_of:            '{done}/{total} done',
        in_progress_count:      '{count} in progress',
        overdue_count:          '{n} overdue | {n} overdue',
        view_list:              'List',
        view_kanban:            'Kanban',
        view_gantt:             'Gantt',
        view_members:           'Members',
        view_activity:          'Activity',
        forbidden:              'You cannot see this project.',
        not_found:              'The project does not exist.',
        unavailable_help:       'It may have been deleted, or you are not one of its members. Ask the project lead for access.',
        back_to_projects:       'Back to projects',
    },
    task: {
        task:                      'Task',
        key:                       'Key',
        title:                     'Title',
        description:               'Description',
        description_placeholder:   'What needs doing, where the data is …',
        status:                    'Status',
        status_todo:               'To do',
        status_in_progress:        'In progress',
        status_review:             'In review',
        status_done:               'Done',
        status_set:                '{key}: {status}',
        priority:                  'Priority',
        priority_low:              'Low',
        priority_normal:           'Normal',
        priority_high:             'High',
        priority_urgent:           'Urgent',
        assignee:                  'Assignee',
        assignee_help:             'Only a project member can be given the task.',
        assignee_not_member:       'The assignee must be a member of the project.',
        assignee_not_member_label: '{name} (no longer a member)',
        unassigned:                'Unassigned',
        all_statuses:              'All statuses',
        all_assignees:             'All assignees',
        start_date:                'Start',
        due_date:                  'Due',
        start_short:               'Start',
        due_short:                 'Due',
        milestone:                 'Milestone',
        milestone_help:            'A single day with no duration — a diamond on the Gantt.',
        milestone_date:            'Milestone day',
        milestone_needs_date:      'A milestone needs a date.',
        due_before_start:          'The due date cannot be before the start.',
        no_dates:                  'no dates',
        estimate:                  'Estimate (hours)',
        estimate_help:             'Spread over the working days on the timeline.',
        estimate_none:             'No estimate',
        estimate_range:            'Between 0 and 10,000 hours.',
        progress:                  'Progress',
        progress_value:            'Progress: {value} %',
        overdue:                   'Overdue',
        blocked:                   'Blocked',
        blocked_help:              'Waiting for a predecessor that is not done yet.',
        predecessors:              'Predecessors',
        predecessors_help:         'The task can start only the day after all its predecessors (same project) end.',
        predecessors_none:         'No predecessors',
        successors:                'Successors',
        successors_none:           'No task is waiting for this one.',
        dependency_cycle:          'That dependency would create a loop — a task cannot wait for itself.',
        dependency_other_project:  'A predecessor must belong to the same project.',
        tab_details:               'Details',
        tab_comments:              'Comments',
        tab_attachments:           'Attachments',
        tab_activity:              'Activity',
        created_meta:              'Created',
        completed:                 'Completed',
        new_placeholder:           'New task — type a title and press Enter',
        add:                       'Add task',
        created:                   'The task was added.',
        create_failed:             'The task could not be added.',
        search:                    'Search tasks …',
        count:                     '{n} task | {n} tasks',
        empty_filtered:            'No tasks match the filters.',
        saved:                     'The task was saved.',
        save_failed:               'The task could not be saved.',
        nothing_changed:           'Nothing changed.',
        delete:                    'Delete task',
        confirm_delete:            'Delete the task {key} {title}? It is also removed from other tasks’ dependencies.',
        deleted:                   'The task was deleted.',
        delete_failed:             'The task could not be deleted.',
        not_available:             'You cannot open this task — it may have been deleted, or you are not a member of its project.',
        load_failed:               'The task could not be loaded.',
    },
    comment: {
        empty:          'No comments yet.',
        placeholder:    'Write a comment …',
        shortcut:       '⌘/Ctrl + Enter sends',
        add:            'Comment',
        add_failed:     'The comment could not be added.',
        edited:         'edited',
        edit_failed:    'The comment could not be saved.',
        delete:         'Delete comment',
        confirm_delete: 'Delete this comment?',
        deleted:        'The comment was deleted.',
        delete_failed:  'The comment could not be deleted.',
        load_failed:    'The comments could not be loaded.',
    },
    attachment: {
        help:                'Files up to 25 MB.',
        upload:              'Upload a file',
        uploaded:            '{n} file uploaded | {n} files uploaded',
        upload_failed_named: 'The file "{name}" could not be uploaded.',
        too_large:           'The file is larger than 25 MB.',
        too_large_named:     'The file "{name}" is larger than 25 MB.',
        empty:               'No attachments.',
        download:            'Download',
        delete:              'Delete attachment',
        confirm_delete:      'Delete the attachment "{name}"?',
        deleted:             'The attachment was deleted.',
        delete_failed:       'The attachment could not be deleted.',
        load_failed:         'The attachments could not be loaded.',
    },
    kanban: {
        open:         'Open the task',
        move_to:      'Move to',
        card_menu:    'Options for {key}',
        add_to:       'Add a task to "{column}"',
        new_title:    'Title of the new task',
        empty_column: 'No tasks',
        move_failed:  'The task could not be moved.',
        hint_mouse:   'Drag a card to another column or place; a click opens it.',
        hint_touch:   'Hold a card, then drag it; a tap opens it.',
    },
    gantt: {
        task:        'Task',
        today:       'Today',
        zoom_day:    'Day',
        zoom_week:   'Week',
        zoom_month:  'Month',
        empty:       'No task has a date yet. Set a start or a due date on a task.',
        undated:     'Without dates',
        moved:       '{key}: {range}',
        move_failed: 'The dates could not be changed.',
        hint_mouse:  'Drag a bar to move it, or its edge to change its start or due date.',
        hint_touch:  'Hold a bar, then drag it; a tap opens the task.',
    },
    timeline: {
        title:             'Who does what by when',
        subtitle:          'Everybody’s tasks on one calendar, and their load day by day.',
        window:            'Period',
        preset_default:    'A week back, 8 weeks ahead',
        preset_this_week:  'This week',
        preset_two_weeks:  'Next 2 weeks',
        preset_this_month: 'This month',
        preset_quarter:    'Next 3 months',
        preset_custom:     'Custom …',
        from:              'From',
        to:                'To',
        project:           'Project',
        all_projects:      'All projects',
        people:            'People',
        all_people:        'Everybody',
        include_done:      'Include done',
        person:            'Person',
        unassigned:        'Unassigned',
        task_count:        '{n} task | {n} tasks',
        capacity:          '{hours} h/day',
        overloaded_days:   '{n} overloaded day | {n} overloaded days',
        load_tooltip:      '{day}: {hours} h of {capacity} h',
        estimate:          'estimate {hours} h',
        legend_load:       'Load',
        legend_overload:   'Overload',
        empty:             'No dated tasks in this period.',
    },
} satisfies MessageSchema
